import React from 'react';
import type { StageResult } from './types';

const TH = {
  bg: '#FFFFFF', border: '#D6DAE0', headerBg: '#F4F6F9',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#6A4C93',
  sigma: '#C2533A', kic: '#2874A6', h0: '#1E8449',
  fontMono: '"Cascadia Code",Consolas,monospace',
};

type Props = { stage: StageResult };

export default function StressInfoCard({ stage }: Props) {
  const sl = stage.stress_layers;
  if (!sl) return null;

  const rows: { label: string; value: string; color?: string }[] = [
    { label: '目标层 σ_h', value: `${sl.sigma_m_mpa} MPa`, color: TH.sigma },
    { label: '上覆层 σ_h', value: `${sl.sigma_upper_mpa} MPa`, color: TH.sigma },
    { label: '下伏层 σ_h', value: `${sl.sigma_lower_mpa} MPa`, color: TH.sigma },
    { label: '应力差 ↑', value: `${(sl.sigma_upper_mpa - sl.sigma_m_mpa).toFixed(1)} MPa` },
    { label: '应力差 ↓', value: `${(sl.sigma_lower_mpa - sl.sigma_m_mpa).toFixed(1)} MPa` },
    { label: '目标层厚 h₀', value: `${sl.h0_m} m`, color: TH.h0 },
    { label: '弹性模量 E', value: `${sl.E_gpa} GPa` },
    { label: '泊松比 ν', value: `${sl.nu.toFixed(3)}` },
  ];

  // K_IC 行 (如果有)
  const kicRows: typeof rows = [];
  if (sl.kic_m != null) kicRows.push({ label: 'K_IC 目标层', value: `${sl.kic_m} MPa·√m`, color: TH.kic });
  if (sl.kic_u != null) kicRows.push({ label: 'K_IC 上覆层', value: `${sl.kic_u} MPa·√m`, color: TH.kic });
  if (sl.kic_b != null) kicRows.push({ label: 'K_IC 下伏层', value: `${sl.kic_b} MPa·√m`, color: TH.kic });

  // 应力阴影提示 (如果有缝高增长)
  const hasGrowth = (stage.height_growth_up ?? 0) > 0.5 || (stage.height_growth_down ?? 0) > 0.5;

  return (
    <div style={{
      borderTop: `1px solid ${TH.border}`, padding: '6px 10px',
      background: TH.bg, fontSize: 10,
    }}>
      <div style={{ fontWeight: 600, color: TH.accent, marginBottom: 4, fontSize: 10 }}>
        应力分层 & 断裂韧度
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px' }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: TH.textMuted }}>{r.label}</span>
            <span style={{ fontFamily: TH.fontMono, fontWeight: 600, color: r.color || TH.text }}>
              {r.value}
            </span>
          </div>
        ))}
      </div>
      {kicRows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px', marginTop: 3, paddingTop: 3, borderTop: `1px solid ${TH.border}` }}>
          {kicRows.map((r, i) => (
            <div key={`k-${i}`} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: TH.textMuted }}>{r.label}</span>
              <span style={{ fontFamily: TH.fontMono, fontWeight: 600, color: r.color || TH.text }}>
                {r.value}
              </span>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 4, fontSize: 9, color: TH.textMuted, lineHeight: 1.4 }}>
        {hasGrowth
          ? `缝高增长 ↑${(stage.height_growth_up ?? 0).toFixed(1)}m ↓${(stage.height_growth_down ?? 0).toFixed(1)}m — K_IC 遏止效应已生效`
          : '缝高未突破阻挡层 — 应力差 + K_IC 屏障有效'}
      </div>
      <div style={{ marginTop: 3, paddingTop: 3, borderTop: `1px solid ${TH.border}`, display: 'flex', gap: 12, fontSize: 9 }}>
        <span>
          <span style={{ color: TH.textMuted }}>支撑半缝长 </span>
          <span style={{ fontWeight: 700, fontFamily: TH.fontMono, color: '#1E8449' }}>{stage.propped_half_length} m</span>
        </span>
        <span>
          <span style={{ color: TH.textMuted }}>支撑比例 </span>
          <span style={{ fontWeight: 700, fontFamily: TH.fontMono, color: TH.text }}>{stage.half_length > 0 ? ((stage.propped_half_length / stage.half_length) * 100).toFixed(0) : 0}%</span>
        </span>
        <span>
          <span style={{ color: TH.textMuted }}>导流 </span>
          <span style={{ fontWeight: 700, fontFamily: TH.fontMono, color: TH.accent }}>{stage.conductivity} D·mm</span>
        </span>
      </div>
      <div style={{ marginTop: 2, fontSize: 8, color: TH.textMuted }}>
        Stokes 沉降模型 · 支撑剂运移由缝内流速与颗粒沉降速度共同决定
      </div>
      {stage.effectiveness && (
        <div style={{ marginTop: 2, paddingTop: 2, borderTop: `1px solid ${TH.border}`, fontSize: 8, color: TH.textMuted }}>
          有效性 {stage.effectiveness.score}分 ({stage.effectiveness.grade})
          {' · '}支撑{stage.effectiveness.breakdown.propped}
          +效率{stage.effectiveness.breakdown.efficiency}
          +遏止{stage.effectiveness.breakdown.containment}
          +均匀{stage.effectiveness.breakdown.uniformity}
        </div>
      )}
    </div>
  );
}
